import React, { FC, useContext, useState } from 'react';
import { Modal, Spin } from 'antd';
import styled from '@emotion/styled';
import _ from 'lodash';
import OptimizePlanTableHeader, { TableHeader } from '../OptimizePlanModal/OptimizePlanTable/OptimizePlanTableHeader';
import { FormData, OptimisationPlanData } from '../OptimizePlanModal/OptimizePlan';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { tradePromoOptimizationUrls } from 'config/urls';
import BaseInputConstraintSection from './BaseInputConstraintSection';
import { formatNumWithKMBAlt, getConfig, sendNotification } from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';

type Props = {
    open: boolean;
    setOpenOptimizeResultModal: React.Dispatch<React.SetStateAction<boolean>>;
    optimizedPlanId: Number;
};

export interface IOptimisationResult {
    constraints: OptimisationPlanData[];
    base_input: FormData;
}

const CustomModal = styled(Modal)`
    .ant-modal-body {
        padding: 0;
    }
    .ant-modal-content {
        padding: 12px 16px;
        border-radius: 8px !important;
    }
    .ant-modal-header {
        padding: 8px 12px;

        border-bottom: none !important;
    }
    .ant-modal-footer {
        border-top: none !important;
        padding: 16px 0 8px 0;
    }
    .ant-modal-close-icon {
        position: relative;
        top: -2px;
    }
    .ant-modal-title {
        font-weight: 400 !important;
    }
`;

const tableHeaders: TableHeader[] = [
    { key: 'kpi', label: 'KPI', allowSort: false },
    { key: 'optimised', label: 'Optimised', allowSort: false },
    { key: 'pre_snapshot', label: 'Original', allowSort: false },
    { key: 'variance', label: 'Variance', allowSort: false },
];

const OptimizeResultModal: FC<Props> = ({ open, setOpenOptimizeResultModal, optimizedPlanId }) => {
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const { decimalForPercentage, decimalForNonPercentage } = commonConfig ?? {};
    const { loading, data, error } = useCommonApiHandler({
        method: 'get',
        queryKey: ['get_optimisation_result', { optimizedPlanId }],
        queryConfig: { enabled: !!optimizedPlanId && open, cacheTime: 0, staleTime: 0 },
        url: `${tradePromoOptimizationUrls.getOptimizationResult}/${optimizedPlanId}`,
        parserFunction: (res: any) => {
            try {
                if (res?.error) {
                    setOpenOptimizeResultModal(false);
                    sendNotification('error', 'Failed to load optimization results');
                    return { error: true };
                }
                return res;
            } catch (error) {
                setOpenOptimizeResultModal(false);
                throw error;
            }
        },
    });

    return (
        <div>
            <CustomModal
                style={{ borderRadius: 8 }}
                maskClosable={false}
                destroyOnClose
                centered
                open={open}
                title="Optimisation Result"
                width={900}
                footer={null}
                onCancel={() => setOpenOptimizeResultModal(false)}
            >
                {loading ? (
                    <div className=" h-[448px] border border-backgroundGreyAlt6  bg-backgroundGreyAlt6  rounded p-3   flex justify-center items-center  ">
                        <Spin />
                    </div>
                ) : (
                    data && (
                        <>
                            <BaseInputConstraintSection data={data.data} />
                            <div className="border border-[rgba(240,240,240,1)] rounded">
                                <OptimizePlanTableHeader headerColumns={tableHeaders} />

                                <div className="max-h-[300px] overflow-y-auto">
                                    {data.data.constraints.map((item: any) => {
                                        const { label, optimized, original_value } = item;
                                        const isPercentage = label?.includes('%');
                                        const decimalType = isPercentage
                                            ? decimalForPercentage
                                            : decimalForNonPercentage;
                                        const differenceValue = Number(optimized) - Number(original_value);
                                        return (
                                            <div
                                                key={label}
                                                className="flex justify-between gap-4 p-4 border-b border-[rgba(240,240,240,1)] last:border-none items-center"
                                            >
                                                <span className="flex-1">{label}</span>
                                                <span className="flex-1">
                                                    {formatNumWithKMBAlt(optimized ?? 0, decimalType)}
                                                </span>
                                                <div className="flex-1">
                                                    {formatNumWithKMBAlt(original_value ?? 0, decimalType)}
                                                </div>
                                                <div
                                                    className={`flex-1 ${
                                                        differenceValue > 0 ? 'text-successGreen' : 'text-redAlt'
                                                    }`}
                                                >
                                                    {formatNumWithKMBAlt(differenceValue ?? 0, decimalType)}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )
                )}
            </CustomModal>
        </div>
    );
};

export default OptimizeResultModal;
